import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Organization, OrganizationCreate } from '../models/organization.model';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Drone assigned to organization (simplified for dropdown use)
 */
export interface OrganizationDrone {
  id: string;
  name: string;
  serialNumber: string;
  nNumber?: string;
  modelName?: string;
  manufacturerName?: string;
  status: string;
  ownership?: 'adn' | 'organization';
  manufacturerId?: string;
  modelId?: string;
  remoteId?: string;
  firmwareVersion?: string;
}

/**
 * DTO for creating an organization-owned drone
 */
export interface CreateOrganizationDroneDto {
  name: string;
  manufacturerId: string;
  modelId: string;
  serialNumber: string;
  nNumber?: string;
  remoteId?: string;
  firmwareVersion?: string;
  status?: string;
}

/**
 * Member of organization (pilot for spray records)
 */
export interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  role: string;
  joinedAt: string;
}

/**
 * Pilot with valid Part 107 certification (for spray record pilot selection)
 * Includes certification data for compliance snapshots
 */
export interface OrganizationPilot {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  joinedAt: string;
  // Part 107 certification (required for legal drone operation)
  part107Number: string | null;
  part107Expiration: string | null;
  // State applicator certification (for pesticide application)
  stateApplicatorNumber: string | null;
  stateApplicatorState: string | null;
  stateApplicatorExpiration: string | null;
}

/**
 * Organization Service
 * Handles organization CRUD operations via backend API
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private apiUrl = environment.apiUrl;
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);

  /**
   * Create a new organization
   * @param organizationData Organization data to create
   * @returns Promise with created organization
   */
  async createOrganization(organizationData: OrganizationCreate): Promise<Organization> {
    const response = this.http.post<ApiResponse<Organization>>(
      `${this.apiUrl}/organizations`,
      organizationData
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create organization');
    }

    // Automatically set the organization context after creation
    this.organizationContext.setCurrentOrganization(result.data.id);

    return result.data;
  }

  /**
   * Get current user's organization
   * Also sets the organization context if found
   * @returns Promise with organization or null if not found
   */
  async getMyOrganization(): Promise<Organization | null> {
    const response = this.http.get<ApiResponse<Organization>>(
      `${this.apiUrl}/organizations/mine`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to get organization');
    }

    // Set organization context if found
    if (result.data) {
      this.organizationContext.setCurrentOrganization(result.data.id);
    }

    return result.data || null;
  }

  /**
   * Get organization by ID
   * @param id Organization ID
   * @returns Promise with organization
   */
  async getOrganization(id: string): Promise<Organization> {
    const response = this.http.get<ApiResponse<Organization>>(
      `${this.apiUrl}/organizations/${id}`
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get organization');
    }

    return result.data;
  }

  /**
   * Update organization
   * @param id Organization ID
   * @param organizationData Organization data to update
   * @returns Promise with updated organization
   */
  async updateOrganization(id: string, organizationData: Partial<OrganizationCreate>): Promise<Organization> {
    const response = this.http.put<ApiResponse<Organization>>(
      `${this.apiUrl}/organizations/${id}`,
      organizationData
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update organization');
    }

    return result.data;
  }

  /**
   * Get drones assigned to current user's organization
   * @returns Promise with array of drones
   */
  async getMyOrganizationDrones(): Promise<OrganizationDrone[]> {
    const response = this.http.get<ApiResponse<OrganizationDrone[]>>(
      `${this.apiUrl}/organizations/mine/drones`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to get organization drones');
    }

    return result.data || [];
  }

  async getMyOrganizationAdnDrones(): Promise<OrganizationDrone[]> {
    const orgDrones = await this.getMyOrganizationDrones()
    return orgDrones.filter((drone) => drone.ownership === 'adn');
  }

  /**
   * Get members of an organization (pilots)
   * @param orgId Organization ID
   * @returns Promise with array of members
   */
  async getOrganizationMembers(orgId: string): Promise<OrganizationMember[]> {
    const response = this.http.get<ApiResponse<OrganizationMember[]>>(
      `${this.apiUrl}/organizations/${orgId}/members`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to get organization members');
    }

    return result.data || [];
  }

  /**
   * Get members of current user's organization (pilots)
   * @returns Promise with array of members
   */
  async getMyOrganizationMembers(): Promise<OrganizationMember[]> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }

    return this.getOrganizationMembers(orgId);
  }

  /**
   * Get pilots with valid Part 107 certifications for spray records
   * Only returns members who have valid (non-expired) FAA Part 107 certifications.
   * Includes certification data for snapshotting on spray records.
   * @param orgId Organization ID
   * @returns Promise with array of pilots with certifications
   */
  async getOrganizationPilots(orgId: string): Promise<OrganizationPilot[]> {
    const response = this.http.get<ApiResponse<OrganizationPilot[]>>(
      `${this.apiUrl}/organizations/${orgId}/pilots`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to get organization pilots');
    }

    return result.data || [];
  }

  /**
   * Get pilots with valid Part 107 certifications for current user's organization
   * Uses /organizations/mine/pilots endpoint which works for all org members
   * @returns Promise with array of pilots with certifications
   */
  async getMyOrganizationPilots(): Promise<OrganizationPilot[]> {
    const response = this.http.get<ApiResponse<OrganizationPilot[]>>(
      `${this.apiUrl}/organizations/mine/pilots`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to get organization pilots');
    }

    return result.data || [];
  }

  /**
   * Create an organization-owned drone
   * @param orgId Organization ID
   * @param droneData Drone data
   * @returns Promise with created drone
   */
  async createOrganizationDrone(orgId: string, droneData: CreateOrganizationDroneDto): Promise<OrganizationDrone> {
    const response = this.http.post<ApiResponse<OrganizationDrone>>(
      `${this.apiUrl}/organizations/${orgId}/drones`,
      droneData
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create drone');
    }

    return result.data;
  }

  /**
   * Create an organization-owned drone for current user's organization
   * @param droneData Drone data
   * @returns Promise with created drone
   */
  async createMyOrganizationDrone(droneData: CreateOrganizationDroneDto): Promise<OrganizationDrone> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }

    return this.createOrganizationDrone(orgId, droneData);
  }

  /**
   * Update an organization-owned drone
   * @param orgId Organization ID
   * @param droneId Drone ID
   * @param droneData Updated drone data
   * @returns Promise with updated drone
   */
  async updateOrganizationDrone(orgId: string, droneId: string, droneData: Partial<CreateOrganizationDroneDto>): Promise<OrganizationDrone> {
    const response = this.http.put<ApiResponse<OrganizationDrone>>(
      `${this.apiUrl}/organizations/${orgId}/drones/${droneId}`,
      droneData
    );

    const result = await firstValueFrom(response);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update drone');
    }

    return result.data;
  }

  /**
   * Update an organization-owned drone for current user's organization
   * @param droneId Drone ID
   * @param droneData Updated drone data
   * @returns Promise with updated drone
   */
  async updateMyOrganizationDrone(droneId: string, droneData: Partial<CreateOrganizationDroneDto>): Promise<OrganizationDrone> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }

    return this.updateOrganizationDrone(orgId, droneId, droneData);
  }

  /**
   * Delete an organization-owned drone
   * @param orgId Organization ID
   * @param droneId Drone ID
   * @returns Promise with success message
   */
  async deleteOrganizationDrone(orgId: string, droneId: string): Promise<void> {
    const response = this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/organizations/${orgId}/drones/${droneId}`
    );

    const result = await firstValueFrom(response);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete drone');
    }
  }

  /**
   * Delete an organization-owned drone for current user's organization
   * @param droneId Drone ID
   * @returns Promise with success message
   */
  async deleteMyOrganizationDrone(droneId: string): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }

    return this.deleteOrganizationDrone(orgId, droneId);
  }
}
